import type { NoteSourceSentence, VisitNoteSources } from "@rosc/contracts";
import {
  Loader2,
  Pause,
  Play,
  SquarePen,
  TriangleAlert,
  X,
} from "lucide-react";
import type { JSX } from "react";
import { useTranslation } from "react-i18next";
import { V3Button } from "@/components/ui/v3/button";
import { formatMoment, highlightShared } from "@/lib/review/noteSources";
import { useMomentReplay } from "@/lib/review/replay";
import { cn } from "@/lib/utils";
import { useWorkspacePane } from "../v3/workspacePane";

// One card for a note sentence's source (v3.2 `.pop`): the exact transcript
// words with the shared ones highlighted, when and who said them, and
// "Replay this moment" from that time. Used in the desktop popover and in
// the phone sheet.

const DISAGREES = new Set(["number_mismatch", "negation_mismatch"]);

export const SourceCard = ({
  sentence,
  sources,
  onClose,
  onEdit,
  showClose = true,
}: {
  sentence: NoteSourceSentence;
  sources: VisitNoteSources;
  onClose?: () => void;
  // Opens the sentence editor for this sentence (click-to-edit with sources).
  onEdit?: () => void;
  showClose?: boolean;
}): JSX.Element => {
  const { t } = useTranslation();
  const segments = sentence.segments;
  const first = segments[0];
  const last = segments[segments.length - 1];
  const transcript =
    first === undefined ? undefined : sources.transcripts[first.transcript];
  const timed =
    first !== undefined &&
    last !== undefined &&
    first.startMs !== null &&
    last.endMs !== null;
  const audioPath =
    timed && transcript?.audio.available ? transcript.audio.url : null;
  const replay = useMomentReplay(
    audioPath,
    timed ? first.startMs : null,
    timed ? last.endMs : null,
  );
  const disagrees = sentence.issues.some((issue) => DISAGREES.has(issue));
  const speaker = first?.speaker ?? null;
  const recordingLabel =
    first && sources.transcripts.length > 1
      ? t("visit.sources.recording", { number: first.transcript + 1 })
      : null;
  const playing = replay.state === "playing";

  return (
    <div className="font-v3 text-v3-text" data-testid="source-card">
      <div className="mb-2 flex min-h-8 items-center gap-2 text-[12px] text-v3-text-3">
        <span
          className={cn(
            "inline-flex h-[18px] items-center rounded-[5px] px-1.5 text-[10.5px] font-semibold uppercase tracking-[0.02em]",
            disagrees
              ? "bg-v3-warn-tint text-v3-warn-text"
              : sentence.supported
                ? "bg-v3-brand text-white"
                : "bg-v3-surface-3 text-v3-text-2",
          )}
        >
          {disagrees
            ? t("visit.sources.check")
            : sentence.supported
              ? t("visit.sources.title")
              : t("visit.sources.noSourceShort")}
        </span>
        {timed && first.startMs !== null && (
          <span className="font-mono tabular-nums" data-testid="source-time">
            {formatMoment(first.startMs)}
          </span>
        )}
        {speaker !== null && (
          <b className="font-medium text-v3-text-2">
            {t("visit.speakers.speakerNumber", { number: speaker + 1 })}
          </b>
        )}
        {recordingLabel && <span>{recordingLabel}</span>}
        {onEdit && (
          <V3Button
            className="ml-auto h-8 px-2.5 text-[12.5px] lg:h-8 [&_svg]:size-3.5"
            data-testid="source-edit"
            variant="ghost"
            onClick={onEdit}
          >
            <SquarePen aria-hidden />
            {t("v3.workspace.edit")}
          </V3Button>
        )}
        {showClose && onClose && (
          <button
            aria-label={t("visit.sources.close")}
            className={cn(
              "-mr-2 flex size-8 items-center justify-center rounded-full text-v3-text-3 hover:bg-v3-surface-2 hover:text-v3-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-v3-brand-ring",
              !onEdit && "ml-auto",
            )}
            type="button"
            onClick={onClose}
          >
            <X aria-hidden className="size-4" />
          </button>
        )}
      </div>

      {disagrees && (
        <p
          className="mb-2 flex gap-2 rounded-lg bg-v3-warn-tint px-2.5 py-2 text-[13px] leading-[18px] text-v3-warn-text"
          role="note"
        >
          <TriangleAlert aria-hidden className="mt-px size-4 shrink-0" />
          {t("visit.sources.differs")}
        </p>
      )}

      {segments.length > 0 ? (
        <blockquote
          className="text-[14px] leading-[1.55] text-v3-text-2"
          data-testid="source-quote"
        >
          {segments.map((segment, index) => (
            <span key={`${segment.transcript}-${segment.idx}`}>
              {index > 0 && " "}
              {highlightShared(segment.text, sentence.text).map((piece, i) =>
                piece.hit ? (
                  <mark
                    // biome-ignore lint/suspicious/noArrayIndexKey: static pieces
                    key={i}
                    className="rounded-[3px] bg-transparent text-v3-text shadow-[inset_0_-0.55em_0_var(--v3-brand-tint-2)]"
                  >
                    {piece.text}
                  </mark>
                ) : (
                  // biome-ignore lint/suspicious/noArrayIndexKey: static pieces
                  <span key={i}>{piece.text}</span>
                ),
              )}
            </span>
          ))}
        </blockquote>
      ) : (
        <p className="text-[13.5px] leading-[1.5] text-v3-text-2">
          {t("visit.sources.none")}
        </p>
      )}

      {segments.length > 0 && (
        <div className="mt-3 flex min-h-11 items-center gap-2 border-t border-v3-border pt-2.5">
          {audioPath ? (
            <button
              aria-pressed={playing}
              className={cn(
                "relative inline-flex h-9 items-center gap-2 overflow-hidden rounded-full pl-1.5 pr-3.5 text-[13px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-v3-brand-ring",
                playing
                  ? "bg-v3-brand-tint text-v3-brand-strong shadow-[inset_0_0_0_1px_var(--v3-brand-ring)]"
                  : "text-v3-text-2 shadow-v3-hairline-strong hover:bg-v3-surface-2 hover:text-v3-text",
              )}
              data-testid="source-replay"
              type="button"
              onClick={() => {
                if (playing) replay.stop();
                else {
                  void replay.play();
                  // Desktop: the transcript pane follows the moment (its
                  // player and the highlighted segment).
                  const pane = useWorkspacePane.getState();
                  if (pane.docked) pane.show("transcript");
                }
              }}
            >
              <span
                aria-hidden
                className="flex size-6 items-center justify-center rounded-full bg-v3-ink text-v3-on-ink"
              >
                {replay.state === "loading" ? (
                  <Loader2 className="size-3 animate-spin" />
                ) : playing ? (
                  <Pause className="size-3" fill="currentColor" />
                ) : (
                  <Play className="size-3 translate-x-px" fill="currentColor" />
                )}
              </span>
              {playing ? t("visit.sources.stop") : t("visit.sources.replay")}
              {playing && (
                <span
                  aria-hidden
                  className="absolute inset-x-0 bottom-0 h-0.5 origin-left bg-v3-brand transition-transform"
                  style={{ transform: `scaleX(${replay.progress})` }}
                />
              )}
            </button>
          ) : (
            <span className="text-[12px] text-v3-text-3">
              {timed ? t("visit.sources.noAudio") : t("visit.sources.noTiming")}
            </span>
          )}
          {timed && first.startMs !== null && last.endMs !== null && (
            <span className="ml-auto font-mono text-[11.5px] tabular-nums text-v3-text-3">
              {formatMoment(first.startMs)}–{formatMoment(last.endMs)}
            </span>
          )}
          {replay.state === "error" && (
            <span className="text-[12px] text-v3-rec-text" role="alert">
              {t("visit.sources.replayFailed")}
            </span>
          )}
        </div>
      )}
    </div>
  );
};
